import React from "react";

import { ThemeMode } from "./themes";

// Colors: cyan, blue, indigo, violet, pink, orange, yellow-orange, cyan
const gradientClass =
  "bg-[conic-gradient(#22d3ee,#355fcd,#3c49ea,#8b5cf6,#f04078,#f78c3a,#fbbf24,#22d3ee)]";

export interface GradientAIButtonProps {
  label: string;
  materialIcon?: string;
  /** When set, shown instead of {@link materialIcon} (nav bar custom sparkle). */
  leadingImageSrc?: string;
  /** When set, shown instead of {@link leadingImageSrc} / {@link materialIcon} (e.g. theme-aware SVG). */
  leadingIcon?: React.ReactNode;
  disabled?: boolean;
  containerClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Active theme mode. Dark mode inverts the pill to white with near-black ink. */
  mode: ThemeMode;
}

export const GradientAIButton: React.FC<GradientAIButtonProps> = ({
  label,
  materialIcon,
  leadingImageSrc,
  leadingIcon,
  disabled,
  containerClassName,
  onClick,
  mode,
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        "group/ai-btn relative p-[2px] rounded-[4px] bg-transparent overflow-hidden shrink-0 " +
        "glow-none hover:glow-primary focus:outline-none focus:glow-primary " +
        `transition duration-400 ${containerClassName || ""}`
      }
    >
      {/* Rotating conic gradient overlay that fades in and spins on hover */}
      <div
        className={
          "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] aspect-ratio-1/1 " +
          `transition duration-400 ${gradientClass} bg-cover bg-center aspect-square animate-[spin_2s_linear_infinite] ` +
          "opacity-0 group-hover/ai-btn:opacity-100 group-focus/ai-btn:opacity-100 " +
          "[animation-play-state:paused] group-hover/ai-btn:[animation-play-state:running] group-focus/ai-btn:[animation-play-state:running]"
        }
      />
      {/* `.themed-primary-button` supplies background, text colour AND both hover colours via
          @apply, so dark mode has to override all four — leaving the inherited hover would flip the
          white pill back to blue-with-white-text mid-interaction.
          #1E1F23 is applied with `!` because the override and the class's own `text-*`/`bg-*` are
          plain utilities in the same Tailwind layer, where the winner would otherwise come down to
          emit order. #f2f2f2 matches the codebase's surface-hover convention (rgba(0,0,0,0.05)
          over white, cf. hoverBgOnSurface in buildThemeFromSpec) — a light tint rather than the
          heavy deepening the blue button uses, which reads as far too loud on a white pill.
          The leading sparkle fills with `currentColor`, so the text colour carries the icon too. */}
      <span
        className={`themed-primary-button rounded-[4px] transition duration-400 ${
          mode === "dark"
            ? "!bg-white !text-[#1E1F23] group-hover/ai-btn:!bg-[#f2f2f2] group-hover/ai-btn:!text-[#1E1F23]"
            : // Light mode forces WHITE text rather than taking the theme's derived
              // `primary-button-foreground`, which is readableText(primary) and therefore resolves to
              // near-black on palettes with a pale primary (Reef, Coral, Dusk) — so the button read
              // dark on some themes and light on others. Hover is set too: the class bakes in
              // `hover:text-primary-button-hover-foreground`, which would otherwise flip it back.
              "!text-white group-hover/ai-btn:!text-white group-hover/ai-btn:bg-primary-button-hover-background"
        }`}
      >
        <span className="flex items-center gap-1.5 text-[13px] font-semibold leading-[18px]">
          {leadingIcon ? (
            <span className="inline-flex shrink-0" aria-hidden>
              {leadingIcon}
            </span>
          ) : leadingImageSrc ? (
            <img
              src={leadingImageSrc}
              alt=""
              className="h-6 w-6 shrink-0 object-contain opacity-90"
              aria-hidden
            />
          ) : (
            materialIcon && <span className="material-icons !text-lg">{materialIcon}</span>
          )}
          <span className="hidden lg:inline">{label}</span>
        </span>
      </span>
    </button>
  );
};
